import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import { useBackTo } from "/imports/ui/components/discovery";
import { useUrlFilters, dateUtils } from "/imports/libs/url";
import usePackageOffers from "/imports/hooks/usePackageOffers";
import usePackComposer from "/imports/hooks/usePackComposer";
import {
  TIME_BUCKETS,
  bucketIdOf,
  buildWeekDates,
  bookingWindowMaxDate,
  ALL_DURATIONS,
  groupSlotsByTime,
  getServiceDurationOptions,
  filterGroupsByPreferredTime,
  pickDefaultSport,
  resolveSlotSetOffer
} from "/imports/ui/components/booking";
import { offerRowKey } from "../PackagesView/packagesHelpers";
import { getOptionsLabel, packWindow } from "./lessonHelpers";
import useLessonReschedule from "./useLessonReschedule";
import useLessonAvailability from "./useLessonAvailability";
import useLessonPreferences from "./useLessonPreferences";
import useLessonSlotPicking from "./useLessonSlotPicking";

export default function useLessonBooking() {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { slug, services, sports, locations, selectedLocation, addToCart, currency } = useWidget();
  const goBack = useBackTo(`/${slug}`);
  const { isAuthenticated } = useAuth();

  const { filters, setFilter, setFilters } = useUrlFilters({
    date: dateUtils.today(),
    duration: ""
  });

  // Pack-composition mode (?packProduct=…): slot taps toggle into a pack
  // selection instead of booking, and a bottom tray drives the purchase.
  const pack = usePackComposer();

  // Reschedule mode (?reschedule=<bookingId>): the times a paid lesson can move to, with
  // the service and duration pinned to the lesson's own. Bumping reloadKey refetches.
  const [reloadKey, setReloadKey] = useState(0);
  const reschedule = useLessonReschedule({ slug, bookingId: filters.reschedule || null, onStale: () => setReloadKey((k) => k + 1) });

  const [selectedSlot, setSelectedSlot] = useState(null);

  // Desktop view state.
  const [viewMode, setViewMode] = useState("day"); // desktop only: 'day' | 'week'
  const [weekStart, setWeekStart] = useState(() => dateUtils.weekStart(new Date())); // ISO Monday
  const weekDates = useMemo(() => buildWeekDates(weekStart), [weekStart]);

  const lessonServices = services?.filter((s) => s.categoryType === "category-service-private") || [];

  const { loading, error, availability, packageOffers, weekSlotsByDate } = useLessonAvailability({
    slug,
    filters,
    selectedLocation,
    isMobile,
    viewMode,
    weekDates,
    pack,
    reschedule,
    reloadKey,
    lessonServices
  });

  const availableSports = useMemo(() => {
    const sportIds = new Set(lessonServices.map((s) => s.sportId).filter(Boolean));
    return (sports || []).filter((s) => sportIds.has(s._id));
  }, [lessonServices, sports]);

  const sportFilteredServices = useMemo(() => {
    if (!filters.sport) return lessonServices;
    return lessonServices.filter((s) => s.sportId === filters.sport);
  }, [lessonServices, filters.sport]);

  const availableTrainers = availability?.trainers || [];

  // Whether the club lets clients pick a specific trainer for this service. When
  // off, the picker is replaced by an inventory panel and a time chip books the
  // trainer/court that keeps the schedule tightest. Absent flag = allowed
  // (legacy services).
  const allowTrainerPick = sportFilteredServices.find((s) => s._id === filters.serviceId)?.widgetAllowResourcePick !== false;

  // Drop a specific trainer carried in the URL (shared link, coach profile,
  // service switch) when the selected service doesn't allow picking one.
  useEffect(() => {
    if (!allowTrainerPick && filters.trainerId) setFilter("trainerId", null);
  }, [allowTrainerPick, filters.trainerId, setFilter]);

  // Drop a trainer who doesn't teach at the location just picked: the search would ask
  // for them there, come back empty, and leave the Trainer field blank.
  useEffect(() => {
    if (loading || !filters.trainerId || !availability?.trainers) return;
    if (!availability.trainers.some((t) => t._id === filters.trainerId)) setFilter("trainerId", null);
  }, [loading, availability, filters.trainerId, setFilter]);

  // Duration options come straight from the selected service's pricing
  // (client-side), so the picker is populated without an availability fetch.
  // Duration is required before anything is fetched; an effect below auto-selects
  // a default (60 min, else the longest option) so the client lands on results.
  const durationOptions = useMemo(
    () =>
      getServiceDurationOptions(
        (sportFilteredServices || []).find((s) => s._id === filters.serviceId),
        filters.locationId
      ),
    [sportFilteredServices, filters.serviceId, filters.locationId]
  );

  // Only the locations the selected service is actually offered at — picking any
  // other org location just yields "No availability". Fall back to all locations
  // for legacy services that don't pin a location set.
  // A lesson being moved goes only where its payment can follow it — the server says where.
  const rescheduleLocationIds = reschedule.info?.locationIds;
  const serviceLocations = useMemo(() => {
    if (rescheduleLocationIds) return (locations || []).filter((l) => rescheduleLocationIds.includes(l._id));
    const svc = (sportFilteredServices || []).find((s) => s._id === filters.serviceId);
    const ids = svc?.locationIds || [];
    if (!ids.length) return locations || [];
    return (locations || []).filter((l) => ids.includes(l._id));
  }, [sportFilteredServices, filters.serviceId, locations, rescheduleLocationIds]);

  // Last selectable date under the service's booking window (null = unlimited).
  // The server drops out-of-window slots regardless; this bounds the pickers.
  // Pack mode additionally caps at the pack's validity window.
  const serviceMaxDate = useMemo(
    () => bookingWindowMaxDate((sportFilteredServices || []).find((s) => s._id === filters.serviceId)),
    [sportFilteredServices, filters.serviceId]
  );
  // Pack mode: the booking window bounds the FIRST pick — a pack buyer enters
  // the timetable on the same terms as everyone else. Once that pick exists the
  // pack's own validity takes over, and it may reach well past the window: a
  // 2-month pack whose first lesson lands on the last open day still has three
  // more lessons to place. The server applies the same rule to the slots.
  const packModeMaxDate = !pack.active
    ? serviceMaxDate
    : pack.hasFirstPick
      ? pack.packMaxDate || serviceMaxDate
      : pack.packMaxDate && serviceMaxDate && serviceMaxDate < pack.packMaxDate
        ? serviceMaxDate
        : pack.packMaxDate || serviceMaxDate;

  // Credit the client already holds reaches past the club's public window: a
  // pack with sessions left and two months to run must be spendable across all
  // of them, not just the part of them the window happens to cover. The server
  // answers how far (it knows which packs cover this service and length) and
  // opens the slots to match; the strip just has to follow it there.
  const ownedPackHorizon = availability?.ownedPackHorizon || null;
  // The rest of what the page says about that credit: how many packs are behind
  // it, whether the date is a real expiry or the one-year ceiling, and where it
  // opens when that is still ahead.
  const ownedPackCount = availability?.ownedPackCount || 0;
  const ownedPackCapped = availability?.ownedPackCapped || false;
  const ownedPackStart = availability?.ownedPackStart || null;

  // Land the page on one day, in both the day strip and the week grid. A jump must
  // work even when the date is already the one being jumped to: the client may have
  // paged the strip away from it, and the date alone not changing would leave the
  // strip where it is.
  const [dateRevealKey, setDateRevealKey] = useState(0);
  const jumpToDate = (iso) => {
    setWeekStart(dateUtils.weekStart(new Date(`${iso}T00:00:00`)));
    setFilter("date", iso);
    setDateRevealKey((k) => k + 1);
  };

  // Unlike the classes timetable, this page has a date filter — so the jump can
  // land on the day itself rather than the week it happens to sit in.
  const goToPackStart = () => {
    if (ownedPackStart) jumpToDate(ownedPackStart);
  };

  // Buying the next pack of a series already booked ahead: the server says where
  // the client's current lessons end, and the next pack carries on from the day
  // after. The grid opens there once; earlier days stay open for anyone who wants
  // to start sooner.
  const packContinuation = pack.active ? availability?.packContinuation || null : null;
  const goToPackContinuation = () => {
    if (packContinuation) jumpToDate(packContinuation.continueFrom);
  };
  const continuationOpened = useRef(false);
  useEffect(() => {
    if (continuationOpened.current || !packContinuation) return;
    continuationOpened.current = true;
    if (pack.committed.length === 0) goToPackContinuation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packContinuation]);
  // The server tells us the last date this request will ever answer for — it is
  // the one that applied the cutoff, and it knows all three rules at once (the
  // club's window, the pack being bought, credit already held). Working it out
  // here meant looking the service up in the widget's own list, and a lookup
  // that missed produced null — read by the strip as "no limit", so it paged on
  // into dates that could only ever come back empty.
  //
  // "" is the server saying there is genuinely no limit; null is "not answered
  // yet", where the local guess still holds the fort until the first payload.
  //
  // A lesson being moved waits for the server: its own package can reach past the public
  // window, and the local guess would snap the page off the lesson's own day.
  const serverMaxDate = availability?.maxBookableDate;
  const localMaxDate = ownedPackHorizon && (!packModeMaxDate || ownedPackHorizon > packModeMaxDate) ? ownedPackHorizon : packModeMaxDate;
  const maxBookableDate = serverMaxDate === "" ? null : serverMaxDate || (reschedule.active ? null : localMaxDate);

  // A lesson moved with a package can't land before the package starts: those days are
  // left off the strip altogether rather than shown and explained.
  const packStart = packWindow(reschedule.info)?.start || null;
  const minBookableDate = packStart && packStart > dateUtils.today() ? packStart : null;

  // Pack mode pins the filters the variant dictates: the linked service, its
  // sport, the sold tier's duration, and (when the purchase fixed one) the
  // location. Batched so the service/sport coherence effects never fight it.
  useEffect(() => {
    if (!pack.resolved) return;
    const next = {};
    const linked = lessonServices.find((s) => s._id === pack.opt.linkedServiceId);
    if (linked?.sportId && filters.sport !== linked.sportId) next.sport = linked.sportId;
    if (filters.serviceId !== pack.opt.linkedServiceId) next.serviceId = pack.opt.linkedServiceId;
    if (pack.opt.sessionDuration && filters.duration !== String(pack.opt.sessionDuration)) next.duration = String(pack.opt.sessionDuration);
    if (pack.packLocationId && filters.locationId !== pack.packLocationId) next.locationId = pack.packLocationId;
    if (Object.keys(next).length > 0) setFilters(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pack.resolved, pack.opt, filters.sport, filters.serviceId, filters.duration, filters.locationId]);

  // Reschedule mode pins the lesson's own service, its sport and its duration — the move
  // never changes them. Batched for the same reason as the pack pin above.
  // Until the server has answered, the service the dashboard put in the URL stands in.
  const rescheduleFrom = reschedule.info?.from;
  const pinnedServiceId = rescheduleFrom?.serviceId || (reschedule.active ? filters.serviceId : null);
  useEffect(() => {
    if (!pinnedServiceId) return;
    const next = {};
    const service = lessonServices.find((s) => s._id === pinnedServiceId);
    if (service?.sportId && filters.sport !== service.sportId) next.sport = service.sportId;
    if (filters.serviceId !== pinnedServiceId) next.serviceId = pinnedServiceId;
    if (rescheduleFrom && filters.duration !== String(rescheduleFrom.duration)) next.duration = String(rescheduleFrom.duration);
    if (Object.keys(next).length > 0) setFilters(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedServiceId, rescheduleFrom, lessonServices.length, filters.sport, filters.serviceId, filters.duration]);

  // Open the grid on the trainer and location of the lessons the club already
  // booked, when those all agree. Waits for the pin above to settle so the two
  // URL writes can't overwrite each other, and only fills what the client
  // hasn't chosen themselves.
  const arrivalFiltersApplied = useRef(false);
  useEffect(() => {
    const target = pack.arrivalFilters;
    if (arrivalFiltersApplied.current || !target || !pack.resolved || filters.serviceId !== pack.opt.linkedServiceId || serviceLocations.length === 0)
      return;
    arrivalFiltersApplied.current = true;
    const next = {};
    if (!filters.locationId && serviceLocations.some((l) => l._id === target.locationId)) next.locationId = target.locationId;
    if (!filters.trainerId && allowTrainerPick) next.trainerId = target.trainerId;
    if (Object.keys(next).length > 0) setFilters(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pack.arrivalFilters, pack.resolved, filters.serviceId, serviceLocations]);

  // Force a concrete sport selection so the Sport filter is never empty and the
  // chip layout stays predictable (every filter always has a value/position).
  // Reschedule mode takes the lesson's own sport instead (above).
  useEffect(() => {
    if (reschedule.active) return;
    if (availableSports.length > 0 && !filters.sport) {
      setFilter("sport", pickDefaultSport(availableSports)._id);
    }
  }, [availableSports, filters.sport, setFilter]);

  // Keep the selected service valid for the chosen sport: auto-pick the first
  // matching service on load and re-pick if the current one no longer matches.
  // A resolved pack owns the service (pinned above) — even one that isn't in the
  // online list, which this effect would otherwise swap out for ever, refetching
  // availability on every flip.
  useEffect(() => {
    if (reschedule.active || pack.resolved || sportFilteredServices.length === 0) return;
    const valid = sportFilteredServices.some((s) => s._id === filters.serviceId);
    if (!filters.serviceId || !valid) {
      setFilter("serviceId", sportFilteredServices[0]._id);
    }
  }, [sportFilteredServices, filters.serviceId, pack.resolved, setFilter]);

  // Clear a stale duration the selected service doesn't offer (e.g. carried in
  // the URL after switching services) so the picker falls back to its empty
  // "choose a duration" state rather than silently querying an invalid value.
  useEffect(() => {
    if (reschedule.active || !filters.duration || !durationOptions.length) return;
    if (filters.duration === ALL_DURATIONS) {
      // "All" of one length is just that length.
      if (durationOptions.length === 1) setFilter("duration", String(durationOptions[0].value));
      return;
    }
    if (!durationOptions.some((o) => String(o.value) === filters.duration)) {
      setFilter("duration", "");
    }
  }, [durationOptions, filters.duration, setFilter]);

  // Auto-select a default duration once a service is chosen. Several lengths:
  // "All durations" — the client picks the time first and the length in the
  // picker, so a start hidden for one length still shows for the others (gap
  // minimization). One length: that one. Pack mode pins the variant's length,
  // so it keeps a concrete default (60, else the longest). Only fills an empty
  // value, so an explicit choice (or a URL param) is preserved.
  useEffect(() => {
    if (reschedule.active || filters.duration || !durationOptions.length) return;
    if (durationOptions.length > 1 && !pack.active) {
      setFilter("duration", ALL_DURATIONS);
      return;
    }
    const has60 = durationOptions.some((o) => o.value === 60);
    const pick = has60 ? 60 : Math.max(...durationOptions.map((o) => o.value));
    setFilter("duration", String(pick));
  }, [durationOptions, filters.duration, pack.active, setFilter]);

  // Picks survive date changes by design — that is what makes the composer
  // usable — but they must not survive the range shrinking under them. Dropping
  // the pick the extension was measured from pulls the horizon back to the
  // club's own window, and anything picked out in the extra weeks is suddenly
  // unbookable: the tray would still offer it, and Continue would fail at the
  // server's own first-pick guard with the client none the wiser.
  useEffect(() => {
    if (!pack.active || !maxBookableDate || pack.picks.length === 0) return;
    const stranded = pack.picks.filter((p) => p.date > maxBookableDate);
    if (stranded.length === 0) return;

    stranded.forEach((p) => pack.removePick(p.key));
    enqueueSnackbar(
      stranded.length === 1
        ? "One of your sessions is past what this pack can book now — it's been removed"
        : `${stranded.length} of your sessions are past what this pack can book now — they've been removed`,
      { variant: "warning" }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxBookableDate, pack.active, pack.picks]);

  // Moving to a lesson before the package starts is impossible — land on its first day.
  useEffect(() => {
    if (minBookableDate && filters.date < minBookableDate) jumpToDate(minBookableDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minBookableDate, filters.date]);

  // Drop a date (or week) that fell outside the booking window — e.g. carried
  // in the URL or left over after switching to a shorter-window service.
  // Clamp the reset target: dateUtils.today() is a UTC calendar date and can
  // itself exceed the device-local bound (same-day windows on devices behind
  // UTC), which would leave the view stuck on an unbookable date.
  useEffect(() => {
    if (!maxBookableDate) return;
    if (filters.date > maxBookableDate) {
      const today = dateUtils.today();
      setFilter("date", today > maxBookableDate ? maxBookableDate : today);
    }
    if (weekStart > maxBookableDate) setWeekStart(dateUtils.weekStart(new Date()));
  }, [maxBookableDate, filters.date, weekStart, setFilter]);

  // Keep the location selection coherent with the service. When the service is
  // offered at a single location, lock the filter to it so its name stays
  // visible (rather than a confusing "All Locations" that hides where it is).
  // Otherwise just drop a stale location the service doesn't serve.
  useEffect(() => {
    if (serviceLocations.length === 1) {
      if (filters.locationId !== serviceLocations[0]._id) setFilter("locationId", serviceLocations[0]._id);
      return;
    }
    if (filters.locationId && !serviceLocations.some((l) => l._id === filters.locationId)) {
      setFilter("locationId", null);
    }
  }, [serviceLocations, filters.locationId, setFilter]);

  // Selected-day groups (mobile).
  const groupedSlots = useMemo(() => groupSlotsByTime(availability?.slots), [availability?.slots]);

  const { preferences, canApplyPreferences, applyPreferences, trainerOptions } = useLessonPreferences({
    slug,
    lessonServices,
    availableSports,
    availableTrainers,
    serviceLocations,
    locations,
    filters,
    setFilters,
    reschedule
  });

  // "Suggested for you" — the loaded slots that fall inside the client's
  // preferred time windows for the selected day. Derived client-side, no fetch.
  const suggestedGroups = useMemo(
    () => filterGroupsByPreferredTime(groupedSlots, preferences.timeRanges, filters.date),
    [groupedSlots, preferences.timeRanges, filters.date]
  );

  // Per-day groups for the desktop week grid.
  const weekGrouped = useMemo(() => {
    const m = new Map();
    if (weekSlotsByDate) {
      for (const [d, slots] of weekSlotsByDate) m.set(d, groupSlotsByTime(slots));
    }
    return m;
  }, [weekSlotsByDate]);

  // Split the time groups into non-empty time-of-day buckets for the mobile
  // segmented control.
  const bucketedSlots = useMemo(() => {
    const byId = new Map();
    for (const group of groupedSlots) {
      const id = bucketIdOf(group.time);
      if (!byId.has(id)) byId.set(id, []);
      byId.get(id).push(group);
    }
    return TIME_BUCKETS.filter((b) => byId.has(b.id)).map((b) => ({ ...b, groups: byId.get(b.id) }));
  }, [groupedSlots]);

  const {
    expandedGroup,
    activeSheet,
    setActiveSheet,
    optionsAnchor,
    optionsGroup,
    optionsDate,
    handleSlotSelect,
    handleGroupClick,
    handleWeekChipClick,
    closeOptionsPopover,
    closeSheet
  } = useLessonSlotPicking({
    slug,
    filters,
    weekStart,
    groupedSlots,
    pack,
    reschedule,
    allowTrainerPick,
    isMobile,
    isAuthenticated,
    addToCart,
    navigate,
    enqueueSnackbar,
    setSelectedSlot
  });

  // A pack that undercuts what these lessons cost, when the client doesn't
  // already hold one. It belongs to the whole result set rather than to any one
  // time — the tiles carry no price of their own — so it is shown once, under
  // the Duration filter that states the price it beats.
  const findPackageOffer = usePackageOffers(packageOffers);
  const offerSlots = useMemo(
    () => (weekSlotsByDate ? [...weekSlotsByDate.values()].flat() : availability?.slots || []),
    [weekSlotsByDate, availability?.slots]
  );
  const packageOffer = useMemo(() => resolveSlotSetOffer(offerSlots, findPackageOffer), [offerSlots, findPackageOffer]);

  // Buying happens on Plans, so the nudge deep-links there and hands over the
  // way back to this exact search — same filters, same day — for Plans to
  // return the client to once the pack is paid for.
  const handleViewPackage = () => {
    if (!packageOffer) return;
    const backTo = `${location.pathname}${location.search}`;
    navigate(`/${slug}/plans?highlight=${encodeURIComponent(offerRowKey(packageOffer))}&from=${encodeURIComponent(backTo)}`);
  };

  const handleSportChange = (sportId) => {
    const matching = sportId ? lessonServices.filter((s) => s.sportId === sportId) : lessonServices;
    setFilters({ sport: sportId, serviceId: matching[0]?._id || filters.serviceId, trainerId: null, duration: "" });
  };

  const goPrevWeek = () => setWeekStart(DateTime.fromISO(weekStart).minus({ days: 7 }).toISODate());
  // A week starting past the booking window has nothing bookable — stop there.
  const canGoNextWeek = !maxBookableDate || DateTime.fromISO(weekStart).plus({ days: 7 }).toISODate() <= maxBookableDate;
  const goNextWeek = () => {
    if (canGoNextWeek) setWeekStart(DateTime.fromISO(weekStart).plus({ days: 7 }).toISODate());
  };

  // ---- Mobile filter chip + bottom-sheet definitions ----------------------
  const selectedService = sportFilteredServices.find((s) => s._id === filters.serviceId);
  const selectedTrainer = trainerOptions.find((t) => t._id === filters.trainerId);
  const selectedLocationObj = locations?.find((l) => l._id === filters.locationId);
  const selectedSport = availableSports.find((s) => s._id === filters.sport);

  const sheetTitles = {
    sport: "Select Sport",
    service: "Select Service",
    trainer: "Select Trainer",
    duration: "Select Duration",
    location: "Select Location",
    options: expandedGroup ? `${expandedGroup.time} · Choose your option` : "Choose your option"
  };

  return {
    navigate,
    goBack,
    slug,
    currency,
    isMobile,
    isAuthenticated,
    filters,
    setFilter,
    setFilters,
    loading,
    error,
    availability,
    setSelectedSlot,
    viewMode,
    setViewMode,
    weekStart,
    weekDates,
    weekGrouped,
    maxBookableDate,
    minBookableDate,
    ownedPackHorizon,
    ownedPackCount,
    ownedPackCapped,
    ownedPackStart,
    goToPackStart,
    jumpToDate,
    packContinuation,
    goToPackContinuation,
    dateRevealKey,
    canGoNextWeek,
    lessonServices,
    availableSports,
    sportFilteredServices,
    availableTrainers,
    trainerOptions,
    allowTrainerPick,
    durationOptions,
    serviceLocations,
    groupedSlots,
    suggestedGroups,
    bucketedSlots,
    expandedGroup,
    activeSheet,
    setActiveSheet,
    optionsAnchor,
    optionsGroup,
    optionsDate,
    canApplyPreferences,
    applyPreferences,
    // No pack-offer nudge while composing a pack purchase, or moving a lesson already paid for.
    packageOffer: pack.active || reschedule.active ? null : packageOffer,
    handleViewPackage,
    pack,
    reschedule,
    handleSlotSelect,
    handleSportChange,
    handleGroupClick,
    handleWeekChipClick,
    closeOptionsPopover,
    goPrevWeek,
    goNextWeek,
    closeSheet,
    getOptionsLabel: (slots) => getOptionsLabel(slots, serviceLocations.length > 1, filters.duration === ALL_DURATIONS),
    selectedService,
    selectedTrainer,
    selectedLocationObj,
    selectedSport,
    sheetTitles
  };
}
